import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAxios, useSubjectMember } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { isAfter } from 'date-fns'
import { type FC, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Flex, FlexColumn, Padding } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { globalDefinitions } from '@/defined-translations'
import { useHealthProfile } from '@/modules/app/hooks/useHealthProfile'
import { useHealthParameterUpdate } from '@/modules/profile/state/useHealthParameterUpdate'
import { QuestionObservation } from '@/modules/questionnaire/components/question-observation/QuestionObservation'
import { buildCareplanActivityQuestionnaireUrl } from '@/modules/questionnaire/utils/questionnaire.utils'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { useToast } from '@/modules/shared/hooks/useToast'

import type { HealthParameter } from '@/modules/app/types/HealthParameter.types'
import type { QuestionnaireCareplanActivity } from '@/modules/questionnaire/types/questionnaire.types'

export const GeneralHealthParameterSection: FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const member = useSubjectMember()
  const { showErrorToast } = useToast()

  const { data: healthProfile, isLoading: healthProfileIsLoading, error: healthProfileError } = useHealthProfile()

  const hbAxios = useAxios()

  const { healthParameterUpdateState } = useHealthParameterUpdate()

  // Template variables
  // ------------------------------------------------------------------------- /
  const hasUnprocessedUpdates = useMemo(() => {
    if (
      healthParameterUpdateState &&
      healthProfile?.healthParameters
        .map((x) => x.name)
        .includes(healthParameterUpdateState.slug as HealthParameter['name']) &&
      healthParameterUpdateState?.member === member?.id
    ) {
      const profileHealthParameter = healthProfile?.healthParameters?.find(
        (x) => x.name === healthParameterUpdateState.slug,
      )

      if (profileHealthParameter?.lastUpdatedAt) {
        const updateDate = new Date(healthParameterUpdateState.timestamp)
        const profileDate = new Date(profileHealthParameter.lastUpdatedAt)

        // Check if the update timestamp is newer than the profile's lastUpdatedAt
        return isAfter(updateDate, profileDate)
      }

      return !!healthParameterUpdateState
    }
  }, [healthParameterUpdateState, healthProfile])

  // Interaction methods
  // ------------------------------------------------------------------------- /
  const openQuestionnaire = async (slug: string) => {
    try {
      const { data: healthParameterQuestionnaire } = await hbAxios.post<QuestionnaireCareplanActivity>(
        '/views/patient/health-profile/questionnaire-activity',
        {
          healthParameterSlug: slug,
          subjectId: member?.id,
        },
      )

      const url = buildCareplanActivityQuestionnaireUrl(healthParameterQuestionnaire)

      if (url) {
        navigate(url)
      }
    } catch (error) {
      console.error(error)
      showErrorToast({
        message: i18n._('healthparameter.error', undefined, {
          message: 'Er is een fout opgetreden bij het wijzigen van de gezondheidsgegevens',
        }),
      })
    }
  }

  // Render
  // ------------------------------------------------------------------------- /

  if (healthProfileIsLoading) {
    return (
      <Flex justifyContent='center' alignItems='center'>
        <AnimatedLoader small={true} />
      </Flex>
    )
  }

  if (healthProfileError || !healthProfile) {
    return (
      <Span type='BodyMediumRegular' color={theme.Colors.Status.Error}>
        {t({
          id: 'error.general',
          message: 'Er is een fout opgetreden',
        })}
      </Span>
    )
  }

  return (
    <FlexColumn gap={theme.UI.Spacing.Space6}>
      {hasUnprocessedUpdates ? (
        <FlexColumn
          gap={theme.UI.Spacing.Space4}
          style={{
            background: theme.Colors.Neutrals.Lighter,
            padding: theme.UI.Spacing.Space3,
          }}
        >
          <Span type='BodySmallRegular' color={theme.Colors.Neutrals.Darker}>
            {t({
              id: 'healthparameter.changed',
              message:
                'Je meldde een wijziging op {date}. Het kan enkele minuten duren voordat de aanpassing zichtbaar is.',
              values: {
                date: formatLocale(new Date(healthParameterUpdateState?.timestamp), 'dd MMMM yyyy'),
              },
            })}
          </Span>
        </FlexColumn>
      ) : null}

      {healthProfile.healthParameters.map((healthParameter) => {
        return (
          <FlexColumn
            gap={theme.UI.Spacing.Space1}
            key={healthParameter.name}
            style={{ cursor: 'pointer' }}
            onClick={() => openQuestionnaire(healthParameter.name)}
          >
            <Flex justifyContent='space-between' alignItems='center'>
              {(globalDefinitions[healthParameter.name as keyof typeof globalDefinitions] ||
                healthParameter.display) && (
                <H3 noMargin>
                  {globalDefinitions[healthParameter.name as keyof typeof globalDefinitions]
                    ? i18n._(globalDefinitions[healthParameter.name as keyof typeof globalDefinitions] as string)
                    : healthParameter.display}
                </H3>
              )}
              <FontAwesomeIcon icon={['far', 'chevron-right']} color={theme.Components.Button.Tertiary.Text} />
            </Flex>
            <QuestionObservation key={healthParameter.name} healthParameter={healthParameter} />
          </FlexColumn>
        )
      })}

      <Padding py={theme.UI.Spacing.Space20} />
    </FlexColumn>
  )
}
